import React, { useState } from "react"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose
} from "../../../ui/drawer"
import { Button } from "../../../ui/button"
import { ScrollArea } from "../../../ui/scroll-area"
import { MessageSquare, Send, X } from "lucide-react"
import { useToast } from "../../../hooks/use-toast"

const ChatDrawer = ({ open, onOpenChange }) => {
  const { toast } = useToast()
  const [message, setMessage] = useState("")
  const [messages, setMessages] = useState([
    {
      id: "1",
      content: "👋 Hi there! How can we help you today?",
      sender: "support",
      timestamp: new Date()
    }
  ])

  const handleSendMessage = e => {
    e.preventDefault()

    if (!message.trim()) return

    // Add user message
    const userMessage = {
      id: `user-${Date.now()}`,
      content: message,
      sender: "user",
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setMessage("")

    // Simulate agent response after a short delay
    setTimeout(() => {
      const supportMessage = {
        id: `support-${Date.now()}`,
        content:
          "Thanks for your message! A member of our team will get back to you shortly. Is there anything else I can help you with?",
        sender: "support",
        timestamp: new Date()
      }

      setMessages(prev => [...prev, supportMessage])
      toast({
        title: "New message",
        description: "You received a new message from support"
      })
    }, 1000)
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[80vh] max-h-[80vh] sm:max-w-[425px] sm:rounded-lg sm:h-[600px] sm:max-h-[80vh]">
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-[#E5DEFF] flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-[#3D2A60]" />
              </div>
              <div>
                <DrawerTitle>Live Support</DrawerTitle>
                <DrawerDescription>Our team is here to help</DrawerDescription>
              </div>
            </div>
            <DrawerClose className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-gray-100">
              <X className="h-4 w-4" />
            </DrawerClose>
          </div>
        </DrawerHeader>

        <ScrollArea className="flex-1 p-4 h-[calc(100%-170px)]">
          <div className="space-y-4">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                    msg.sender === "user"
                      ? "bg-[#3D2A60] text-white"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  <p>{msg.content}</p>
                  <p
                    className={`text-[10px] mt-1 ${
                      msg.sender === "user" ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DrawerFooter className="border-t">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Type your message here..."
              className="flex-1 border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#3D2A60]"
            />
            <Button type="submit" className="bg-[#3D2A60] hover:bg-[#2A1C42]">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default ChatDrawer
